import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * The `MultiSelectEntity` function is a React component that renders a multi-select dropdown list of
 * entities with search functionality, optimized for handling large datasets.
 */
function MultiSelectEntity(props) {
  const { searchBox=true } = props;
  const [searchText, setSearchText] = useState("");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [visibleCount, setVisibleCount] = useState(100); // Initial visible count

  // Only re-compute filtered entities when primaryEntity or searchText changes
  const filteredEntities = useMemo(() => {
    if (!searchText) {
      return props.primaryEntity || [];
    }
    
    const lowerSearchText = searchText.toLowerCase();
    return (props.primaryEntity || []).filter(
      entity => entity?.name?.toLowerCase()?.includes(lowerSearchText)
    );
  }, [props.primaryEntity, searchText]);
  
  // Calculate selected count once
  const selectedCount = useMemo(() => 
    (props.primaryEntity || []).filter(option => option.isChecked).length,
  [props.primaryEntity]);
  
  // Memoize event handlers to prevent unnecessary re-renders
  const handleSearchTextChange = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);
    setVisibleCount(100); // Reset visible count when search changes
    if (props.setCitySearchText) {
      props.setCitySearchText(value);
    }
  }, [props.setCitySearchText]);
  
  const handleSelectAll = useCallback(() => {
    const newCheckedState = !selectAllChecked;
    setSelectAllChecked(newCheckedState);

    // Get IDs of enabled (not disabled) entities
    const enabledIds = filteredEntities
      .filter(entity => !props?.disabled?.some(disabledItem => disabledItem.id === entity.id))
      .map(entity => entity.id);

    if (searchText === '') {
      // When no search, select all enabled entities only
      props.handleEntityChange({
        target: { name: 'filteredSelect', checked: newCheckedState, filteredIds: enabledIds },
      });
    } else {
      // For filtered entities, select all enabled filtered entities only
      props.handleEntityChange({
        target: { name: 'filteredSelect', checked: newCheckedState, filteredIds: enabledIds },
      });
    }
  }, [selectAllChecked, searchText, filteredEntities, props.disabled, props.handleEntityChange]);
  
  // Calculate enabled (not disabled) entities
  const enabledEntities = useMemo(() =>
    filteredEntities.filter(entity => !props?.disabled?.some(disabledItem => disabledItem.id === entity.id)),
    [filteredEntities, props.disabled]
  );

  // Update selectAllChecked state based on enabled entities
  useEffect(() => {
    if (enabledEntities.length === 0) {
      setSelectAllChecked(false);
      return;
    }
    const enabledSelectedCount = enabledEntities.filter(option => option.isChecked).length;
    setSelectAllChecked(enabledSelectedCount === enabledEntities.length && enabledEntities.length > 0);
  }, [enabledEntities]);
  
  // Handle scrolling to load more items
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      // When user scrolls to 2/3 of the visible area, load more
      setVisibleCount(prev => Math.min(prev + 50, filteredEntities.length));
    }
  }, [filteredEntities.length]);
  
  // Only render a subset of entities at a time to improve performance
  const visibleEntities = useMemo(() => {
    return filteredEntities.slice(0, visibleCount);
  }, [filteredEntities, visibleCount]);
  
  // Show load message if we have more items than what's being shown
  const hasMoreItems = filteredEntities.length > visibleCount;
  
  // Determine placeholder text
  const placeholderText = useMemo(() => {
    if (props.city === "city") {
      return selectedCount === 0 ? "Select city" : 
             selectedCount > 1 ? `${selectedCount} cities selected` : 
             `${selectedCount} city selected`;
    } else if (props.city === "country") {
      return selectedCount === 0 ? "Select country" : 
             selectedCount > 1 ? `${selectedCount} countries selected` : 
             `${selectedCount} country selected`;
    }
    return "Search";
  }, [selectedCount, props.city]);
  
  // Determine what message to show when no entities are available
  const getEmptyMessage = () => {
    // If search is active but no results, show "No results found"
    if (searchText && (props.primaryEntity?.length > 0)) {
      return 'No results found!';
    }
    
    // If field is sector and no search, show "Choose Industry"
    if (props?.field === 'sector') {
      return 'First Select Industry';
    }
    
    // Default message
    return 'No results found!';
  };

  console.log((props.primaryEntity?.length > 3 && searchBox),"uiyy")
  
  return (
    <div className="multiselect-container">
      {(props.primaryEntity?.length > 3 && searchBox) && (
        <input
          type="search"
          value={searchText}
          onChange={handleSearchTextChange}
          className="input w-[98%] border-b-2 border-black my-2 p-2 pt-0 focus:outline-none"
          placeholder={placeholderText}
        />
      )}
      
      {enabledEntities?.length !== 1 && filteredEntities && filteredEntities.length > 0 && (props.showAll ?? true) && (
        <div
          className="form-check font-sans_medium_body cursor-default flex text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 md:w-auto hover:bg-gray-50"
        >
          <input
            type="checkbox"
            className="form-check-input"
            name="allSelect"
            id="0"
            checked={selectAllChecked}
            onChange={handleSelectAll}
          />
          <label htmlFor='0' className={"form-check-label mx-2 mr-2 cursor-pointer text-[13px] text-[#333333] leading-[17px] font-sans_book_body"}>
            {selectAllChecked ? "Deselect All" : "Select All"}
          </label>
        </div>
      )}
      
      <div className={`entity-list ${props.round ? "grid grid-cols-3" : visibleEntities.length <= 10 ? "grid grid-cols-2" : "grid grid-cols-1"}`}>
        {visibleEntities && visibleEntities.length > 0 ? (
          <>
            {(() => {
              const enabledEntities = props?.primaryEntity?.filter(
                entity => !props?.disabled?.some(disabledItem => disabledItem.id === entity.id)
              );

              // Split items into two columns for vertical layout
              const midPoint = Math.ceil(visibleEntities.length / 2);
              const firstColumn = visibleEntities.slice(0, midPoint);
              const secondColumn = visibleEntities.slice(midPoint);

              const renderColumn = (entities, isFirstColumn) => (
                <div className={`${props.round ? (isFirstColumn ? "col-span-1" : "col-span-2") : ""}`}>
                  {entities.map((entity, index) => {
                    const isDisabled = props?.disabled?.some(disabledItem => disabledItem.id === entity.id);
                    const isChecked = !isDisabled && enabledEntities.length <= 1 || entity.isChecked;

                    return (
                      <div
                        key={entity.id || index}
                        className="form-check font-sans_medium_body cursor-default flex items-center text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 md:w-auto hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name={entity.name}
                          id={entity.id}
                          checked={isChecked}
                          onChange={props.handleEntityChange}
                          disabled={isDisabled}
                        />
                        <label
                          htmlFor={entity.id}
                          className={`${
                            isDisabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer text-black"
                          } form-check-label mx-2 mr-2 text-[13px] text-[#333333] leading-[17px] font-sans_book_body`}
                        >
                          {entity.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              );

              return (
                <>
                  {renderColumn(firstColumn, true)}
                  {secondColumn.length > 0 && renderColumn(secondColumn, false)}
                </>
              );
            })()}
          </>
        ) : (
          <h1 className="text-[13px] text-[#333333] leading-[17px] font-sans_book_body p-2">
            {getEmptyMessage()}
          </h1>
        )}
      </div>
      
      {hasMoreItems && (
        <div className="py-2 text-center text-gray-500 text-sm w-full">
          Showing {visibleCount} of {filteredEntities.length} items
          {searchText ? " (filtered)" : ""}
        </div>
      )}
    </div>
  );
}

export default React.memo(MultiSelectEntity);